import type { AxiosInstance } from "axios";

import type { DeleteServiceArgs } from "api/services/appointment/hooks/useDeleteService";
import type { PostAppointmentV2NoShow } from "api/services/appointment/hooks/usePostAppointmentV2NoShow";
import { validateResponse } from "api/validateResponse";

import type { DeleteAppointmentV2Args } from "./hooks/useDeleteAppointmentV2";
import type { DeleteAppointmentV2ManagerArgs } from "./hooks/useDeleteAppointmentV2Manager";
import type { GetAppointmentsV2Args } from "./hooks/useGetAppointmentsV2";
import type { PostAppointmentV1Payload } from "./schemas/appointment_v1";
import { AppointmentV1Schema } from "./schemas/appointment_v1";
import type {
  PostAppointmentV2Args,
  PostAppointmentV2ManagerArgs,
  PutAppointmentV2Args,
  PutAppointmentV2ManagerArgs,
} from "./schemas/appointment_v2";
import { AppointmentV2Schema, AppointmentsV2ResponseSchema } from "./schemas/appointment_v2";
import type {
  DeleteBookingIntervalArgs,
  PostBookingIntervalArgs,
  PostManagerBookingIntervalArgs,
  PutBookingIntervalArgs,
} from "./schemas/booking_interval";
import type { HcpService, PostServiceArgs, PutServiceArgs } from "./schemas/hcp_service";
import { HcpServiceManagerSchema, HcpServiceSchema, ServiceTemplateSchema } from "./schemas/hcp_service";

interface CreateAppointmentServiceArgs {
  transportV1: AxiosInstance;
  transportV2: AxiosInstance;
}

export const createAppointmentService = ({ transportV1, transportV2 }: CreateAppointmentServiceArgs) => ({
  // BOOKING v1
  async getAppointmentsV1(therapistID: number) {
    const params = { filter: "since_today" };
    const response = await transportV1.get(`/therapists/${therapistID}/appointments`, { params });
    return validateResponse(response, AppointmentV1Schema.array(), false);
  },

  async postAppointmentV1(args: PostAppointmentV1Payload) {
    const response = await transportV1.post("/appointments", args);
    return validateResponse(response, AppointmentV1Schema, false);
  },

  async putAppointmentCompleted(appointmentID: number) {
    const response = await transportV1.put(`/appointments/${appointmentID}`, { state: "completed" });
    return validateResponse(response, AppointmentV1Schema, false);
  },

  // BOOKING v2 - appointments
  deleteAppointmentV2(args: DeleteAppointmentV2Args) {
    const { hcProfId, appointmentId } = args;
    return transportV2
      .delete(`/health_care_professionals/${hcProfId}/appointments/${appointmentId}`)
      .then(({ data }) => data);
  },

  async getAppointmentsV2(args: GetAppointmentsV2Args) {
    const { userId, startDate, view } = args;
    const response = await transportV2.get(
      `/health_care_professionals/${userId}/appointments?start_date=${startDate}&view=${view}`
    );
    return validateResponse(response, AppointmentsV2ResponseSchema, false);
  },

  async getAppointmentsV2Patient(userId: number, patientId: number) {
    const response = await transportV2.get(`/health_care_professionals/${userId}/patients/${patientId}/appointments`);
    return validateResponse(response, AppointmentV2Schema.array(), false);
  },

  async postAppointmentV2(args: PostAppointmentV2Args) {
    const { data: body, hcProfId } = args;
    const response = await transportV2.post(`/health_care_professionals/${hcProfId}/appointments`, body);
    return validateResponse(response, AppointmentV2Schema, false);
  },

  postAppointmentV2NoShow(args: PostAppointmentV2NoShow) {
    const { appointmentID, hcProfID } = args;
    return transportV2
      .post(`/health_care_professionals/${hcProfID}/appointments/${appointmentID}/no-show`)
      .then(({ data }) => data);
  },

  async putAppointmentV2(args: PutAppointmentV2Args) {
    const { data: body, hcProfId, appointmentId } = args;
    return transportV2
      .put(`/health_care_professionals/${hcProfId}/appointments/${appointmentId}/reschedule`, body)
      .then(({ data }) => data);
  },

  // BOOKING v2 - appointments (manager)
  deleteAppointmentV2Manager(args: DeleteAppointmentV2ManagerArgs) {
    const { managerId, appointmentId } = args;
    return transportV2.delete(`/managers/${managerId}/appointments/${appointmentId}`).then(({ data }) => data);
  },

  async getAppointmentsV2Manager(args: GetAppointmentsV2Args) {
    const { userId, startDate, view } = args;
    const response = await transportV2.get(`/managers/${userId}/appointments?start_date=${startDate}&view=${view}`);
    return validateResponse(response, AppointmentsV2ResponseSchema, false);
  },

  async postAppointmentV2Manager(args: PostAppointmentV2ManagerArgs) {
    const { data: body, managerId } = args;
    const response = await transportV2.post(`/managers/${managerId}/appointments`, body);
    return validateResponse(response, AppointmentV2Schema, false);
  },

  async putAppointmentV2Manager(args: PutAppointmentV2ManagerArgs) {
    const { data: body, managerId, appointmentId } = args;
    const response = await transportV2.put(`/managers/${managerId}/appointments/${appointmentId}/reschedule`, body);
    return validateResponse(response, AppointmentV2Schema, false);
  },

  // BOOKING v2 - booking interval
  deleteBookingInterval(args: DeleteBookingIntervalArgs) {
    const { hcProfID, bookingIntervalID } = args;
    return transportV2
      .delete(`/health_care_professionals/${hcProfID}/booking_intervals/${bookingIntervalID}`)
      .then(({ data }) => data);
  },

  async postBookingInterval(args: PostBookingIntervalArgs) {
    const { data: body, hcProfID } = args;
    return transportV2.post(`/health_care_professionals/${hcProfID}/booking_intervals`, body).then(({ data }) => data);
  },

  putBookingInterval(args: PutBookingIntervalArgs) {
    const { data: body, hcProfID, bookingIntervalID } = args;
    return transportV2
      .put(`/health_care_professionals/${hcProfID}/booking_intervals/${bookingIntervalID}`, body)
      .then(({ data }) => data);
  },

  async postManagerBookingInterval(args: PostManagerBookingIntervalArgs) {
    const { data: body, managerID, hcProfID } = args;
    return transportV2
      .post(`/managers/${managerID}/health_care_professionals/${hcProfID}/booking_intervals`, body)
      .then(({ data }) => data);
  },

  // BOOKING v2 - services
  async deleteService(args: DeleteServiceArgs) {
    const { managerId, serviceId } = args;
    return transportV2.delete(`/managers/${managerId}/services/${serviceId}`).then(({ data }) => data);
  },

  async getHcpServices(userId: number): Promise<HcpService[]> {
    const response = await transportV2.get(`/health_care_professionals/${userId}/services`);
    return validateResponse(response, HcpServiceSchema.array(), false);
  },

  async getManagerServices(managerId: number) {
    const response = await transportV2.get(`/managers/${managerId}/services`);
    return validateResponse(response, HcpServiceManagerSchema.array(), false);
  },

  async getServiceTemplate(managerId: number) {
    const response = await transportV2.get(`/managers/${managerId}/service_template`);
    return validateResponse(response, ServiceTemplateSchema, false);
  },

  async postService(args: PostServiceArgs) {
    const { data: body, managerId } = args;
    const response = await transportV2.post(`/managers/${managerId}/services`, body);
    return validateResponse(response, HcpServiceManagerSchema, false);
  },

  async putService(args: PutServiceArgs) {
    const { data: body, managerId } = args;
    const response = await transportV2.put(`/managers/${managerId}/services`, body);
    return validateResponse(response, HcpServiceManagerSchema, false);
  },
});

export type AppointmentService = ReturnType<typeof createAppointmentService>;
